import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    FormControlLabel,
    Switch,
    Alert,
    CircularProgress,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Send as SendIcon,
    Schedule as ScheduleIcon,
} from '@mui/icons-material';
import {
    useEmailConfigStatusQuery,
    useEmailLogQuery,
    useSendTestEmailMutation,
} from '../../../api/JsdhNotifikaceService/service/EmailNastaveniMutations';
import type { Konfigurace } from '../../../api/JsdhNotifikaceService/generated';
import type { useUpdateAdminKonfiguraceMutation } from '../../../api/JsdhNotifikaceService/service/AdminMutations';
import dayjs from 'dayjs';

interface EmailNastaveniTabProps {
    konfigurace: Konfigurace[] | undefined;
    updateKonfiguraceMutation: ReturnType<typeof useUpdateAdminKonfiguraceMutation>;
    refetchKonfigurace: () => void;
}

const EmailNastaveniTab: React.FC<EmailNastaveniTabProps> = ({
    konfigurace,
    updateKonfiguraceMutation,
    refetchKonfigurace,
}) => {
    const getVal = (key: string) => konfigurace?.find(k => k.klic === key)?.hodnota;

    const emailEnabled = getVal('notifikace_email_enabled') === '1';
    const [replyTo, setReplyTo] = useState(getVal('notifikace_email_reply_to') || '');
    const [replyToDirty, setReplyToDirty] = useState(false);
    const [testEmailTo, setTestEmailTo] = useState('');

    const { data: configStatus, isLoading: isLoadingStatus } = useEmailConfigStatusQuery();
    const { data: emailLog, isLoading: isLoadingLog } = useEmailLogQuery({ limit: 20 });
    const sendTestMutation = useSendTestEmailMutation();

    useEffect(() => {
        const val = getVal('notifikace_email_reply_to') || '';
        setReplyTo(val);
        setReplyToDirty(false);
    }, [konfigurace]);

    const handleEmailEnabledToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateKonfiguraceMutation.mutate(
            { klic: 'notifikace_email_enabled', hodnota: e.target.checked ? '1' : '0' },
            { onSuccess: () => refetchKonfigurace() }
        );
    };

    const handleSaveReplyTo = () => {
        updateKonfiguraceMutation.mutate(
            { klic: 'notifikace_email_reply_to', hodnota: replyTo },
            { onSuccess: () => { refetchKonfigurace(); setReplyToDirty(false); } }
        );
    };

    const handleSendTest = () => {
        if (testEmailTo.trim()) {
            sendTestMutation.mutate({ to: testEmailTo.trim() });
        }
    };

    const statusChipForLog = (status: 'PENDING' | 'SENT' | 'FAILED') => {
        if (status === 'SENT') return <Chip label="Odesláno" color="success" size="small" icon={<CheckCircleIcon />} />;
        if (status === 'FAILED') return <Chip label="Chyba" color="error" size="small" icon={<ErrorIcon />} />;
        return <Chip label="Čeká" color="warning" size="small" icon={<ScheduleIcon />} />;
    };

    return (
        <>
            {/* SMTP stav */}
            <Paper sx={{ mb: 3, p: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Stav SMTP
                </Typography>
                {isLoadingStatus ? (
                    <CircularProgress size={24} />
                ) : configStatus ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {configStatus.smtpConfigured ? (
                                <CheckCircleIcon color="success" fontSize="small" />
                            ) : (
                                <ErrorIcon color="error" fontSize="small" />
                            )}
                            <Typography variant="body2">
                                SMTP: {configStatus.smtpConfigured ? 'nakonfigurováno' : 'není nakonfigurováno'}
                            </Typography>
                        </Box>
                        {configStatus.fromAddress && (
                            <Typography variant="body2" color="text.secondary">
                                Odesílatel: {configStatus.fromAddress}
                            </Typography>
                        )}
                        {configStatus.error && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                                {configStatus.error}
                            </Alert>
                        )}
                    </Box>
                ) : (
                    <Alert severity="warning">Stav SMTP není dostupný.</Alert>
                )}
            </Paper>

            {/* Globální nastavení e-mailu */}
            <Paper sx={{ mb: 3, p: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Globální nastavení e-mailových notifikací
                </Typography>

                {/* §7.1 upozornění */}
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                        Zabezpečení přílohy (§ 7.1)
                    </Typography>
                    <Typography variant="body2">
                        PDF přílohy (lékařské posudky) jsou u členů s vyplněným datem narození automaticky
                        zaheslovány (heslo = datum narození ve formátu <strong>DDMMRRRR</strong>, příklad:
                        07041985 pro 7. dubna 1985). Členové bez vyplněného data narození dostanou přílohu
                        nezaheslovanou s prázdným datem — to doplní ručně na vytištěném formuláři
                        (doporučujeme datum doplnit do profilu).
                    </Typography>
                </Alert>

                <FormControlLabel
                    control={
                        <Switch
                            checked={emailEnabled}
                            onChange={handleEmailEnabledToggle}
                            disabled={updateKonfiguraceMutation.isPending}
                        />
                    }
                    label="Povolit e-mailové notifikace kontrol"
                    sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', maxWidth: 480 }}>
                    <TextField
                        label="Reply-To adresa"
                        size="small"
                        fullWidth
                        value={replyTo}
                        onChange={(e) => { setReplyTo(e.target.value); setReplyToDirty(true); }}
                        type="email"
                        helperText="Adresa, na kterou člen odpoví (nepovinné)"
                        disabled={!emailEnabled || updateKonfiguraceMutation.isPending}
                    />
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleSaveReplyTo}
                        disabled={!replyToDirty || updateKonfiguraceMutation.isPending || !emailEnabled}
                        sx={{ whiteSpace: 'nowrap', mt: 0.5 }}
                    >
                        Uložit
                    </Button>
                </Box>
            </Paper>

            {/* Test e-mail */}
            <Paper sx={{ mb: 3, p: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Testovací e-mail
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', maxWidth: 480 }}>
                    <TextField
                        label="Odeslat testovací e-mail na"
                        size="small"
                        fullWidth
                        value={testEmailTo}
                        onChange={(e) => setTestEmailTo(e.target.value)}
                        type="email"
                        placeholder="vas@email.cz"
                        disabled={sendTestMutation.isPending}
                    />
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={
                            sendTestMutation.isPending ? <CircularProgress size={14} /> : <SendIcon />
                        }
                        onClick={handleSendTest}
                        disabled={!testEmailTo.trim() || sendTestMutation.isPending}
                        sx={{ whiteSpace: 'nowrap', mt: 0.5 }}
                    >
                        Odeslat
                    </Button>
                </Box>
            </Paper>

            {/* Log e-mailů */}
            <Paper sx={{ mb: 3, p: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Posledních 20 e-mailů jednotky
                </Typography>
                {isLoadingLog ? (
                    <CircularProgress size={24} />
                ) : !emailLog?.length ? (
                    <Typography variant="body2" color="text.secondary">
                        Žádné e-maily v logu.
                    </Typography>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Příjemce</TableCell>
                                    <TableCell>Předmět</TableCell>
                                    <TableCell>Stav</TableCell>
                                    <TableCell>Odesláno</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {emailLog.map((entry) => (
                                    <TableRow key={entry.id} hover>
                                        <TableCell>
                                            <Typography variant="body2">{entry.recipientEmail}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                {entry.subject}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{statusChipForLog(entry.status)}</TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="text.secondary">
                                                {entry.sentAt
                                                    ? dayjs(entry.sentAt).format('DD.MM.YYYY HH:mm')
                                                    : dayjs(entry.createdAt).format('DD.MM.YYYY HH:mm')}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </>
    );
};

export default EmailNastaveniTab;
